/**
 * Guarded `localStorage` access (JMKW-1808).
 *
 * `localStorage` is not always reachable, and the failure mode is a **throw**, not a null
 * return:
 *
 * - Safari (and some others) throw `SecurityError` on any access in private browsing.
 * - `setItem` throws `QuotaExceededError` when storage is full, even if reads succeed.
 * - The global is absent entirely outside a browser (SSR, workers, some test runners).
 *
 * Unguarded access in a field initializer or during service construction therefore fails
 * the whole service, which in JMKW's case could prevent the map from rendering at all.
 *
 * These helpers never throw. Reads return `null` and writes return `false` when storage is
 * unreachable, so callers can treat "unavailable" exactly like "nothing stored". Each takes
 * an optional `onUnavailable` callback so the caller decides how to log — the utility stays
 * free of any logger dependency and can be used from non-DI contexts.
 */

/** Invoked with the underlying error when an operation could not be completed. */
export type StorageUnavailableHandler = (error: unknown) => void;

/**
 * Invoke the caller's handler without letting it break the never-throws guarantee.
 *
 * The handler is caller-supplied — typically a logger call — so it can itself throw. If that
 * escaped, it would reintroduce precisely the init-time crash this module exists to prevent,
 * only now from the reporting path rather than from storage.
 *
 * The fallback is `console`, not NGXLogger: a throwing handler means the caller's logger is
 * the failing component, so reporting through it again would be pointless. This also keeps
 * the module free of a logger dependency, per the standard for non-DI utilities. Neither
 * error is swallowed — both the storage error and the handler error are reported.
 */
function report(
  onUnavailable: StorageUnavailableHandler | undefined,
  error: unknown
): void {
  try {
    onUnavailable?.(error);
  } catch (handlerError) {
    try {
      console.error(
        '[SafeLocalStorage] onUnavailable handler threw; storage error:',
        error,
        'handler error:',
        handlerError
      );
    } catch {
      // Console itself is unusable. There is nowhere left to report, and throwing
      // here would defeat the whole point of this module.
    }
  }
}

/**
 * Read a value from localStorage.
 *
 * @returns the stored string, or `null` when absent OR storage is unreachable
 */
export function safeGetItem(
  key: string,
  onUnavailable?: StorageUnavailableHandler
): string | null {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    report(onUnavailable, error);
    return null;
  }
}

/**
 * Write a value to localStorage.
 *
 * @returns `true` if persisted, `false` if storage was unreachable or the write was rejected
 */
export function safeSetItem(
  key: string,
  value: string,
  onUnavailable?: StorageUnavailableHandler
): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    report(onUnavailable, error);
    return false;
  }
}

/**
 * Remove a value from localStorage.
 *
 * @returns `true` if the removal completed, `false` if storage was unreachable
 */
export function safeRemoveItem(
  key: string,
  onUnavailable?: StorageUnavailableHandler
): boolean {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    report(onUnavailable, error);
    return false;
  }
}
